import { entraPorMagicLink, type Rol, type SesionRenovada } from '@pasos/schemas';
import { guardarSesion, leerSesion } from './sesion.js';

/**
 * Cliente HTTP de la API.
 *
 * Una sola puerta, por la misma razón que `withTenant` en el backend: si cada
 * pantalla arma su `fetch`, el día que haya que cambiar cómo viaja el token hay
 * que acordarse de todas.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * El servidor **no contestó**: no hubo respuesta que interpretar.
 *
 * Es otra cosa que un error del servidor, y la diferencia se ve en la pantalla
 * (P-99): sin respuesta puede no haber señal, y eso no se arregla reintentando
 * a mano ni tiene nada que ver con el acceso de la persona. Con un 500 sí hubo
 * servidor y la pantalla ofrece reintentar.
 */
export class ErrorDeRed extends Error {
  constructor(causa: unknown) {
    super('sin_respuesta_del_servidor', { cause: causa });
    this.name = 'ErrorDeRed';
  }
}

export class ErrorDeApi extends Error {
  readonly estado: number;
  readonly codigo: string;

  constructor(estado: number, codigo: string) {
    super(codigo);
    this.name = 'ErrorDeApi';
    this.estado = estado;
    this.codigo = codigo;
  }

  /** La sesión se venció o nunca fue válida: hay que volver a entrar. */
  get exigeReingreso(): boolean {
    return this.estado === 401;
  }
}

/**
 * Renueva la sesión con el dispositivo recordado (E3.24).
 *
 * La cookie es httpOnly y la manda el navegador solo: este código no la ve.
 * **Un solo pedido a la vez**: si al abrir la app se disparan tres consultas y
 * las tres reciben 401, se renueva una vez y las tres reintentan con el mismo
 * token. Devuelve si se pudo.
 */
let renovacionEnCurso: Promise<boolean> | null = null;

export function renovarConDispositivo(): Promise<boolean> {
  renovacionEnCurso ??= (async () => {
    try {
      const respuesta = await fetch(`${BASE}/api/v1/auth/dispositivo/renovar`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      // 204: no hay dispositivo recordado en este navegador. 401: había uno y
      // ya no sirve. En los dos casos, no hay renovación. La respuesta se lee
      // igual, aunque venga vacía: Chromium da por abortado un pedido cuya
      // respuesta nadie leyó, y lo anota como fallido.
      if (respuesta.status !== 200) {
        await respuesta.text().catch(() => '');
        return false;
      }
      const sesion = (await respuesta.json()) as SesionRenovada;
      guardarSesion({ idToken: sesion.idToken, rol: sesion.rol });
      return true;
    } catch {
      return false;
    }
  })().finally(() => {
    renovacionEnCurso = null;
  });
  return renovacionEnCurso;
}

/**
 * ¿Vale la pena intentar renovar? Sólo para quien entra sin contraseña, y
 * sólo si ya tenía una sesión: al equipo no se lo renueva por detrás, vuelve a
 * poner su contraseña (S-09).
 */
function puedeRenovar(rol: Rol | undefined): boolean {
  return rol !== undefined && entraPorMagicLink(rol);
}

async function pedir<T>(ruta: string, init?: RequestInit, yaRenovo = false): Promise<T> {
  const sesion = leerSesion();

  let respuesta: Response;
  try {
    respuesta = await fetch(`${BASE}${ruta}`, {
      ...init,
      headers: {
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...(sesion ? { authorization: `Bearer ${sesion.idToken}` } : {}),
        ...init?.headers,
      },
    });
  } catch (causa) {
    // `fetch` sólo rechaza cuando no hubo respuesta: sin red, DNS caído, el
    // pedido cortado. Todo lo demás llega como respuesta, aunque sea un 500.
    throw new ErrorDeRed(causa);
  }

  // El ID token vence a la hora, y la sesión del servidor a los 30 minutos sin
  // uso: al paciente se le renueva sin que se entere, una vez, y se reintenta.
  if (respuesta.status === 401 && !yaRenovo && puedeRenovar(sesion?.rol)) {
    if (await renovarConDispositivo()) return pedir<T>(ruta, init, true);
  }

  if (!respuesta.ok) {
    // El cuerpo de error de la API es `{ error: string }`. Si vino otra cosa, no
    // se inventa un mensaje: se usa el estado.
    const codigo = await respuesta
      .json()
      .then((cuerpo: unknown) =>
        typeof cuerpo === 'object' && cuerpo && 'error' in cuerpo
          ? String((cuerpo as { error: unknown }).error)
          : `http_${respuesta.status}`,
      )
      .catch(() => `http_${respuesta.status}`);

    throw new ErrorDeApi(respuesta.status, codigo);
  }

  // Un 200 con el cuerpo vacío o roto es un error del servidor, no una falta de
  // conexión: hubo respuesta, y lo que la pantalla tiene que ofrecer es
  // reintentar. Sin esto, el `SyntaxError` del parseo se confundía con la falta
  // de red y la persona leía que no tenía señal teniéndola.
  return (await respuesta.json().catch(() => {
    throw new ErrorDeApi(respuesta.status, 'respuesta_ilegible');
  })) as T;
}

export function obtener<T>(ruta: string): Promise<T> {
  return pedir<T>(ruta);
}

export function enviar<T>(ruta: string, cuerpo: unknown): Promise<T> {
  return pedir<T>(ruta, { method: 'POST', body: JSON.stringify(cuerpo) });
}

/**
 * Un POST con un token que **no** es la sesión: el provisorio de la inscripción
 * del segundo factor (E3.24). No se guarda en ningún lado, para que ninguna
 * otra pantalla crea que hay alguien adentro.
 */
export function enviarConToken<T>(ruta: string, cuerpo: unknown, idToken: string): Promise<T> {
  return pedir<T>(ruta, {
    method: 'POST',
    body: JSON.stringify(cuerpo),
    headers: { authorization: `Bearer ${idToken}` },
  });
}
